import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { MessageCircle, Plus, Lock } from 'lucide-react';
import SEO from '@/components/SEO';
import { isPaidTier } from '@/lib/tiers';

const CAT_COLORS = { deals: '#df5681', tiles: '#56dfb4', accessories: '#56c6df', nmjl: '#df5681', other: '#b456df', marketplace: '#56dfb4', social: '#56c6df' };

export default function ForumPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/forum/categories').then(({ data }) => { setCategories(data.categories || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const isPaid = isPaidTier(user);

  const handleCategoryClick = (catId) => {
    if (!user) {
      navigate('/login');
    } else if (!isPaid) {
      navigate('/subscription');
    } else {
      navigate(`/forum/${catId}`);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#56dfb4] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="forum-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title="Discussion Board — American Mahjong Forum"
        description="Ask questions, share strategies, swap stories, and discover deals in the Meet For Mahjong community discussion board. Join the American Mahjong conversation."
        path="/forum"
      />
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Discussion Board</h1>
          <p className="text-slate-500 mt-1">Discuss American Mahjong with fellow players</p>
        </div>
        {isPaid && (
          <Link to="/forum/new">
            <Button className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-2" data-testid="new-thread-btn">
              <Plus className="w-4 h-4" /> New Thread
            </Button>
          </Link>
        )}
      </div>

      {/* Access prompt for non-logged-in users */}
      {!user && (
        <div className="mb-6 p-5 rounded-2xl bg-[#b456df]/5 border border-[#b456df]/20 text-center" data-testid="forum-login-prompt">
          <Lock className="w-10 h-10 mx-auto mb-3 text-[#b456df] opacity-70" />
          <p className="font-medium mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Log in to read and participate in discussions</p>
          <p className="text-sm text-slate-500 mb-4">Browse the topics below, then sign in with a paid membership to read posts and join the conversation.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/login"><Button className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] px-6" data-testid="forum-login-btn">Log In</Button></Link>
            <Link to="/register"><Button variant="outline" className="rounded-full px-6" data-testid="forum-register-btn">Sign Up</Button></Link>
          </div>
        </div>
      )}

      {/* Access prompt for free-tier logged-in users */}
      {user && !isPaid && (
        <div className="mb-6 p-5 rounded-2xl bg-[#b456df]/5 border border-[#b456df]/20 flex items-center gap-4" data-testid="forum-upgrade-prompt">
          <Lock className="w-8 h-8 text-[#b456df] flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Paid membership required</p>
            <p className="text-xs text-slate-500">Upgrade to read posts, create threads, and join discussions.</p>
          </div>
          <Link to="/subscription"><Button className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] px-5" data-testid="forum-upgrade-btn">Upgrade</Button></Link>
        </div>
      )}

      {/* Guidelines */}
      <Card className="border-slate-100 mb-6">
        <CardContent className="p-5">
          <h3 className="font-semibold text-sm mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Forum Guidelines</h3>
          <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
            <li>Each thread must have a clear title and context</li>
            <li>Be respectful and constructive</li>
            <li>No harassment, spam, or sharing personal data</li>
            <li>Provide sources or examples when possible</li>
          </ul>
        </CardContent>
      </Card>

      {/* Categories — visible to everyone */}
      <div className="space-y-4">
        {categories.map(cat => (
          <div key={cat.id} onClick={() => handleCategoryClick(cat.id)} className="cursor-pointer">
            <Card className={`border-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 mb-4 ${!isPaid ? 'opacity-90' : ''}`} data-testid={`forum-cat-${cat.id}`}>
              <CardContent className="p-6 flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${CAT_COLORS[cat.id] || '#56dfb4'}15` }}>
                  <MessageCircle className="w-6 h-6" style={{ color: CAT_COLORS[cat.id] }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold mb-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{cat.name}</h3>
                  </div>
                  <p className="text-sm text-slate-500">{cat.description}</p>
                  {isPaid && cat.latest_thread && (
                    <p className="text-xs text-slate-400 mt-1 truncate">
                      Latest: {cat.latest_thread.title} by {cat.latest_thread.author_name}
                    </p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  {isPaid ? (
                    <>
                      <p className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{cat.thread_count}</p>
                      <p className="text-xs text-slate-400">threads</p>
                    </>
                  ) : (
                    <Lock className="w-5 h-5 text-slate-300" />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}
